import { create } from "zustand";

import type { UnlockedRewardData } from "@/types/loyalty.types";
import { logger } from "@/util/logger";

// Discriminated union for overlay types with proper data typing
export type OverlayQueueItem =
  | {
      id: string;
      type: "loyalty-reward";
      priority: number;
      data: UnlockedRewardData;
      onShow?: () => void;
      onDismiss?: () => void;
    }
  | {
      id: string;
      type: "recalculation";
      priority: number;
      data?: never;
      onShow?: () => void;
      onDismiss?: () => void;
    }
  | {
      id: string;
      type: "quota-exceeded";
      priority: number;
      data?: never;
      onShow?: () => void;
      onDismiss?: () => void;
    }
  | {
      id: string;
      type: "other";
      priority: number;
      data?: unknown;
      onShow?: () => void;
      onDismiss?: () => void;
    };

interface OverlayQueueState {
  queue: OverlayQueueItem[];
  currentOverlay: OverlayQueueItem | null;
  enqueueOverlay: (item: OverlayQueueItem) => void;
  dequeueOverlay: () => void;
  dismissCurrentOverlay: () => void;
  clearOverlayQueue: () => void;
}

/**
 * Overlay Queue Store
 *
 * Manages a priority-based queue for overlays (modals, drawers, etc.) to prevent
 * React Aria overlay stacking conflicts and provide better UX by showing overlays sequentially.
 *
 * Priority levels:
 * - 0: Quota exceeded modal (highest priority, blocks all other actions)
 * - 1: Recalculation modal (user needs to make a decision)
 * - 2: Loyalty reward modal (celebratory, can wait)
 * - 3+: Other modals
 *
 * @example
 * const { enqueueOverlay, dequeueOverlay, currentOverlay } = useOverlayQueueStore();
 *
 * // Enqueue a modal
 * enqueueOverlay({
 *   id: 'recalculation-modal',
 *   type: 'recalculation',
 *   priority: 1,
 * });
 *
 * // Show modal based on queue
 * <Modal
 *   isOpen={currentOverlay?.type === 'recalculation'}
 *   onClose={() => dequeueOverlay()}
 * />
 */
export const useOverlayQueueStore = create<OverlayQueueState>((set, _get) => ({
  queue: [],
  currentOverlay: null,

  /**
   * Add an overlay to the queue
   * - Prevents duplicates with the same ID
   * - Sorts queue by priority (lower number = higher priority)
   * - Automatically shows overlay if nothing is currently displayed
   * - Preempts current overlay if new item has higher priority
   */
  enqueueOverlay: (item: OverlayQueueItem) => {
    set((state) => {
      // Prevent duplicate items with same ID
      const isDuplicate = state.queue.some((existing) => existing.id === item.id);
      if (isDuplicate) {
        logger.info(`[OverlayQueue] Skipping duplicate overlay: ${item.id}`);
        return state;
      }

      // Also check if current overlay has same ID
      if (state.currentOverlay?.id === item.id) {
        logger.info(`[OverlayQueue] Overlay already showing: ${item.id}`);
        return state;
      }

      logger.info(`[OverlayQueue] Enqueuing overlay: ${item.id} (type: ${item.type}, priority: ${item.priority})`);

      // If no overlay is currently showing, add to queue and show the highest priority one
      if (!state.currentOverlay) {
        const newQueue = [...state.queue, item].sort((a, b) => a.priority - b.priority);
        const [nextOverlay, ...remainingQueue] = newQueue;
        logger.info(`[OverlayQueue] Showing overlay: ${nextOverlay.id}`);
        nextOverlay.onShow?.();
        return {
          queue: remainingQueue,
          currentOverlay: nextOverlay,
        };
      }

      // If new item has HIGHER priority (lower number) than current overlay, preempt it
      if (item.priority < state.currentOverlay.priority) {
        logger.info(
          `[OverlayQueue] Preempting current overlay ${state.currentOverlay.id} (priority ${state.currentOverlay.priority}) with ${item.id} (priority ${item.priority})`,
        );

        // Put current overlay back in queue and show the new higher-priority item
        const newQueue = [...state.queue, state.currentOverlay].sort((a, b) => a.priority - b.priority);

        // Dismiss the old overlay
        state.currentOverlay.onDismiss?.();

        // Show the new overlay
        item.onShow?.();

        return {
          queue: newQueue,
          currentOverlay: item,
        };
      }

      // Otherwise, just add to queue (sorted by priority)
      const newQueue = [...state.queue, item].sort((a, b) => a.priority - b.priority);
      return {
        ...state,
        queue: newQueue,
      };
    });
  },

  /**
   * Remove current overlay and show next in queue
   */
  dequeueOverlay: () => {
    set((state) => {
      if (!state.currentOverlay) {
        logger.warn("[OverlayQueue] Attempted to dequeue but no overlay is showing");
        return state;
      }

      logger.info(`[OverlayQueue] Dequeuing overlay: ${state.currentOverlay.id}`);
      state.currentOverlay.onDismiss?.();

      // Show next overlay in queue if available
      if (state.queue.length > 0) {
        const [nextOverlay, ...remainingQueue] = state.queue;
        logger.info(`[OverlayQueue] Showing next overlay: ${nextOverlay.id}`);
        nextOverlay.onShow?.();
        return {
          queue: remainingQueue,
          currentOverlay: nextOverlay,
        };
      }

      // No more overlays in queue
      logger.info("[OverlayQueue] Queue is empty");
      return {
        ...state,
        currentOverlay: null,
      };
    });
  },

  /**
   * Dismiss current overlay without showing next
   * Use this when you want to clear the current overlay but keep the queue
   */
  dismissCurrentOverlay: () => {
    set((state) => {
      if (!state.currentOverlay) {
        return state;
      }

      logger.info(`[OverlayQueue] Dismissing overlay: ${state.currentOverlay.id}`);
      state.currentOverlay.onDismiss?.();

      return {
        ...state,
        currentOverlay: null,
      };
    });
  },

  /**
   * Clear all overlays in queue (keeps current overlay visible)
   */
  clearOverlayQueue: () => {
    set((state) => {
      if (state.queue.length === 0) {
        return state;
      }

      logger.info(`[OverlayQueue] Clearing queue (${state.queue.length} items)`);
      return {
        ...state,
        queue: [],
      };
    });
  },
}));
